import type { ComparisonResult, RunSelection } from 'fuel-benchmark-contracts';
import { FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { StatusText } from '../../components/ui/StatusText';
import { cn } from '../../lib/cn';
import { formatDay, formatDecimal, formatKm, formatMeasurement, formatText } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { reportBuilderLink } from '../reports/report-display';
import { runStatusTone } from '../runs/run-display';
import { powerOutput } from '../vehicles/vehicle-display';
import { checkSpreadOf, checkValuesOf, compatibilityOutcomeTone, scenarioSummary, verdictTone } from './compare-display';

export interface ComparisonPanelProps {
  comparison: ComparisonResult;
  isStale: boolean;
  /** The very selection behind these figures, handed to the report builder untouched. */
  runSelection: RunSelection;
}

const TABLE_HEADER_CLASS = 'whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground';

/**
 * The result panel of plan §7: the aggregated figure per vehicle, the headline difference, the verdict, the
 * conditions fact table the report reuses, the published references kept visibly apart from what was
 * measured, and the runs every number came from.
 */
export const ComparisonPanel = ({ comparison, isStale, runSelection }: ComparisonPanelProps) => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const { vehicles, compatibility, headline, unit } = comparison;
  const baseline = vehicles.find((entry) => entry.vehicle.id === headline.baselineVehicleId);
  const best = vehicles.find((entry) => entry.vehicle.id === headline.bestVehicleId);
  const hasDifference = headline.percentDiff !== null && headline.percentDiff > 0 && best !== undefined && baseline !== undefined;

  const runSummaryOf = (runCount: number, stddev: number | null): string => {
    const runs = runCount === 1 ? t('compare.result.runsOne') : t('compare.result.runsMany', { count: runCount });

    return stddev === null ? `(${runs})` : `(${runs}, σ ${formatDecimal(stddev, 2)})`;
  };

  return (
    <div className={cn('space-y-4', isStale && 'opacity-60 transition-opacity')}>
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-card-foreground">{scenarioSummary(comparison.scenario, t)}</p>
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {comparison.scenario.code} · {comparison.scenario.protocolCode} v{comparison.scenario.protocolVersion}
            </p>
          </div>
          <div className="text-end">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('compare.result.verdict')}</p>
            <StatusText
              className="text-base"
              label={t(`enums.verdict.${compatibility.verdict}`)}
              tone={verdictTone(compatibility.verdict)}
            />
          </div>
        </div>

        <dl className="mt-4 divide-y divide-border border-t border-border">
          {vehicles.map((entry) => (
            <div
              key={entry.vehicle.id}
              className="flex flex-wrap items-baseline justify-between gap-2 py-3"
            >
              <dt className="min-w-0">
                <span className="text-sm font-medium text-foreground">{entry.vehicle.label}</span>
                <span className="ms-2 font-mono text-xs text-muted-foreground">{entry.vehicle.code}</span>
                {entry.isBaseline ? <span className="ms-2 text-xs text-muted-foreground">{t('compare.result.baseline')}</span> : null}
              </dt>
              <dd className="flex flex-wrap items-baseline gap-2">
                <span className="text-lg font-semibold text-foreground">
                  {entry.aggregate.mean === null ? t('common.notSet') : `${formatDecimal(entry.aggregate.mean, 1)} ${entry.unit}`}
                </span>
                <span className="text-xs text-muted-foreground">{runSummaryOf(entry.aggregate.runCount, entry.aggregate.stddev)}</span>
                {entry.percentDiffVsBaseline !== null && entry.percentDiffVsBaseline > 0 ? (
                  <span className="text-sm text-foreground">
                    {t('compare.result.percentLower', { percent: formatDecimal(entry.percentDiffVsBaseline, 1) })}
                  </span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-3 text-sm text-foreground">
          <span className="text-muted-foreground">{t('compare.result.difference')}: </span>
          {hasDifference && best && baseline
            ? t('compare.result.headline', {
                percent: formatDecimal(headline.percentDiff, 1),
                absolute: formatDecimal(headline.absoluteDiff, 2),
                unit,
                vehicle: best.vehicle.label,
                baseline: baseline.vehicle.label,
              })
            : t('compare.result.noDifference')}
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-foreground">{t('compare.result.conditions')}</h2>
        <p className="max-w-3xl text-xs text-muted-foreground">{t('compare.result.conditionsHint')}</p>

        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th
                  scope="col"
                  className={TABLE_HEADER_CLASS}
                >
                  {t('compare.result.factor')}
                </th>
                {vehicles.map((entry) => (
                  <th
                    key={entry.vehicle.id}
                    scope="col"
                    className={TABLE_HEADER_CLASS}
                  >
                    {entry.vehicle.code}
                  </th>
                ))}
                <th
                  scope="col"
                  className={TABLE_HEADER_CLASS}
                >
                  {t('compare.result.spread')}
                </th>
                <th
                  scope="col"
                  className={TABLE_HEADER_CLASS}
                >
                  {t('compare.result.outcome')}
                </th>
              </tr>
            </thead>
            <tbody>
              {compatibility.checks.length === 0 ? (
                <tr>
                  <td
                    colSpan={vehicles.length + 3}
                    className="px-4 py-12 text-center text-muted-foreground"
                  >
                    {t('compare.result.checksEmpty')}
                  </td>
                </tr>
              ) : (
                compatibility.checks.map((check) => (
                  <tr
                    key={check.key}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-4 py-3 text-start">
                      <p className="text-foreground">{t(`compare.checks.${check.key}`, { defaultValue: check.key })}</p>
                      <p className="font-mono text-xs text-muted-foreground">{check.detail.field}</p>
                    </td>
                    {vehicles.map((entry) => (
                      <td
                        key={entry.vehicle.id}
                        className="px-4 py-3 text-start text-foreground"
                      >
                        {checkValuesOf(check, entry.vehicle.id)}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3 text-start text-muted-foreground">{checkSpreadOf(check)}</td>
                    <td className="px-4 py-3 text-start">
                      <StatusText
                        label={t(`enums.compatibilityOutcome.${check.outcome}`)}
                        tone={compatibilityOutcomeTone(check.outcome)}
                      />
                      {check.detail.reason ? <p className="mt-0.5 max-w-xs text-xs text-muted-foreground">{check.detail.reason}</p> : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-foreground">{t('compare.result.runsIncluded')}</h2>
        <div className="grid gap-3 lg:grid-cols-2">
          {vehicles.map((entry) => (
            <div
              key={entry.vehicle.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="truncate text-sm font-medium text-card-foreground">{entry.vehicle.label}</p>
                <p className="shrink-0 text-xs text-muted-foreground">{formatText(powerOutput(entry.vehicle))}</p>
              </div>
              <ul className="mt-2 divide-y divide-border border-t border-border">
                {entry.runs.map((run) => (
                  <li
                    key={run.id}
                    className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm"
                  >
                    <Link
                      to={`/runs/${run.id}`}
                      className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
                    >
                      {formatDay(run.testDate)}
                      <span className="ms-2 font-mono text-xs text-muted-foreground">{run.routeCode ?? ''}</span>
                    </Link>
                    <span className="flex items-baseline gap-3">
                      <span className="text-xs text-muted-foreground">
                        {formatKm(run.distanceTotalKm)} · {formatMeasurement(run.fuelConsumedL, 'L', 1)}
                      </span>
                      <span className="whitespace-nowrap font-medium text-foreground">
                        {run.result === null ? t('common.notSet') : `${formatDecimal(run.result, 1)} ${entry.unit}`}
                      </span>
                      <StatusText
                        className="text-xs"
                        label={t(`enums.runStatus.${run.status}`)}
                        tone={runStatusTone(run.status)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-foreground">{t('compare.result.published')}</h2>
        <p className="max-w-3xl rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {t('compare.result.publishedHint')}
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          {vehicles.map((entry) => (
            <div
              key={entry.vehicle.id}
              className="rounded-lg border border-dashed border-border bg-card p-4"
            >
              <p className="truncate text-sm font-medium text-card-foreground">{entry.vehicle.label}</p>
              {entry.publishedResults.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">{t('compare.result.publishedEmpty')}</p>
              ) : (
                <dl className="mt-2 divide-y divide-border border-t border-border">
                  {entry.publishedResults.map((published) => (
                    <div
                      key={published.id}
                      className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm"
                    >
                      <dt className="text-muted-foreground">
                        {t(`enums.regime.${published.regime}`)}
                        <span className="ms-2 font-mono text-xs">{published.metric}</span>
                      </dt>
                      <dd className="whitespace-nowrap text-foreground">{formatMeasurement(published.value, published.unit, 1)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <Button
          onClick={() =>
            navigate(
              reportBuilderLink(
                vehicles.map((entry) => entry.vehicle.id),
                comparison.scenario.id,
              ),
              { state: { runSelection } },
            )
          }
        >
          <FileText className="size-4" />
          {t('compare.result.generateReport')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('compare.result.reportHint')}</p>
      </div>
    </div>
  );
};
